import { useState } from 'react';
import { App, Button, Card, Popconfirm, Space, Switch, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PencilSimple, Plus, Trash } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageBody, PageHeader } from '../../app/AppLayout';
import { DataTable } from '../../components/DataTable';
import { ErrorState, LoadingState } from '../../components/PageStates';
import { ApiError, NetworkError } from '../../api/client';
import { tokens } from '../../theme';
import {
  customerCareGroupApi,
  customerCareGroupKeys,
  type CustomerCareGroup,
  type UpsertCustomerCareGroupPayload,
} from './api';
import { CustomerCareGroupFormModal } from './CustomerCareGroupFormModal';

export function CustomerCareGroupsPage() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<CustomerCareGroup | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const listQuery = useQuery({
    queryKey: customerCareGroupKeys.list,
    queryFn: customerCareGroupApi.list,
  });

  const reportError = (error: unknown, fallback: string) => {
    message.error(
      error instanceof ApiError || error instanceof NetworkError ? error.message : fallback,
    );
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: customerCareGroupKeys.all });

  const saveMutation = useMutation({
    mutationFn: (payload: { id?: string; body: UpsertCustomerCareGroupPayload }) =>
      payload.id
        ? customerCareGroupApi.update(payload.id, payload.body)
        : customerCareGroupApi.create(payload.body),
    onSuccess: async () => {
      await invalidate();
      setEditing(null);
      setIsCreating(false);
      message.success('Đã lưu nhóm.');
    },
    onError: (error) => reportError(error, 'Không lưu được nhóm.'),
  });

  const toggleMutation = useMutation({
    mutationFn: (row: CustomerCareGroup) =>
      customerCareGroupApi.update(row.id, {
        name: row.name,
        url: row.url,
        sortOrder: row.sortOrder,
        isEnabled: !row.isEnabled,
      }),
    onSuccess: async (saved) => {
      await invalidate();
      message.success(saved.isEnabled ? 'Đã bật nhóm.' : 'Đã tắt nhóm.');
    },
    onError: (error) => reportError(error, 'Không đổi được trạng thái nhóm.'),
  });

  const deleteMutation = useMutation({
    mutationFn: customerCareGroupApi.remove,
    onSuccess: async () => {
      await invalidate();
      message.success('Đã xóa nhóm.');
    },
    onError: (error) => reportError(error, 'Không xóa được nhóm.'),
  });

  const confirmDelete = (row: CustomerCareGroup) => {
    modal.confirm({
      title: `Xóa “${row.name}”?`,
      content: 'Khách không còn thấy nhóm này trên trang Cá nhân.',
      okText: 'Xóa',
      okButtonProps: { danger: true },
      cancelText: 'Hủy',
      onOk: () => deleteMutation.mutateAsync(row.id),
    });
  };

  const columns: ColumnsType<CustomerCareGroup> = [
    {
      title: 'Tên nhóm',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      fixed: 'left',
      render: (name: string) => <span style={{ fontWeight: 600 }}>{name}</span>,
    },
    {
      title: 'Link',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
      render: (url: string) => (
        <Typography.Text style={{ fontFamily: 'monospace', color: tokens.color.ink3 }} ellipsis>
          {url}
        </Typography.Text>
      ),
    },
    {
      title: 'Thứ tự',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 100,
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 150,
      render: (_, row) => (
        <Popconfirm
          title={row.isEnabled ? 'Tắt nhóm này?' : 'Bật lại nhóm này?'}
          description={
            row.isEnabled
              ? 'Khách không còn thấy nhóm này. Link vẫn giữ, bật lại khi cần.'
              : 'Khách sẽ thấy nhóm này trên trang Cá nhân.'
          }
          okText="Đồng ý"
          cancelText="Hủy"
          onConfirm={() => toggleMutation.mutate(row)}
        >
          <Switch
            checked={row.isEnabled}
            loading={toggleMutation.isPending && toggleMutation.variables?.id === row.id}
            checkedChildren="Bật"
            unCheckedChildren="Tắt"
          />
        </Popconfirm>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Button
            size="small"
            icon={<PencilSimple size={14} />}
            aria-label={`Sửa ${row.name}`}
            onClick={() => setEditing(row)}
          >
            Sửa
          </Button>
          <Button
            size="small"
            danger
            icon={<Trash size={14} />}
            aria-label={`Xóa ${row.name}`}
            onClick={() => confirmDelete(row)}
          >
            Xóa
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Nhóm chăm sóc khách hàng"
        subtitle="Cấu hình"
        actions={
          <Button type="primary" icon={<Plus size={16} />} onClick={() => setIsCreating(true)}>
            Thêm nhóm
          </Button>
        }
      />
      <PageBody>
        <Typography.Paragraph
          type="secondary"
          style={{ marginTop: 0, marginBottom: tokens.space.sectionGap }}
        >
          Mỗi dòng là một nhóm Zalo. Khách bấm tham gia trên trang Cá nhân, xác nhận, rồi được mở
          đúng link. Nhóm đang tắt không hiện.
        </Typography.Paragraph>

        {listQuery.isPending && <LoadingState />}
        {listQuery.isError && (
          <ErrorState error={listQuery.error} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.isSuccess && (
          <Card>
            <DataTable
              rowKey="id"
              size="small"
              columns={columns}
              dataSource={listQuery.data}
              onReload={() => void listQuery.refetch()}
              searchPlaceholder="Tìm tên hoặc link nhóm"
              pagination={{ pageSize: 20, showSizeChanger: false }}
              scroll={{ x: 860 }}
              locale={{
                emptyText: 'Chưa có nhóm nào. Khách chưa thấy mục tham gia nhóm trên trang Cá nhân.',
              }}
            />
          </Card>
        )}
      </PageBody>

      <CustomerCareGroupFormModal
        group={editing}
        isCreating={isCreating}
        submitting={saveMutation.isPending}
        onClose={() => {
          setEditing(null);
          setIsCreating(false);
        }}
        onSubmit={(body) => {
          if (isCreating) saveMutation.mutate({ body });
          else if (editing) saveMutation.mutate({ id: editing.id, body });
        }}
      />
    </>
  );
}
